import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

interface UseNavigationPreventionOptions {
  when: boolean;
  pendingAction: string | null;
  setPendingAction: (action: string | null) => void;
}

let navigationWasBlocked = false;
let globalNavigationPreventionEnabled = false;
let globalNavigationCallback: ((to: string, action: 'PUSH' | 'REPLACE' | 'POP') => boolean) | null = null;

export const setNavigationWasBlocked = (blocked: boolean) => {
  navigationWasBlocked = blocked;
};

export const getNavigationWasBlocked = () => navigationWasBlocked;

// Global navigation prevention setup
export const setupGlobalNavigationPrevention = (
  enabled: boolean,
  callback?: (to: string, action: 'PUSH' | 'REPLACE' | 'POP') => boolean
) => {
  globalNavigationPreventionEnabled = enabled;
  globalNavigationCallback = callback || null;
};

export const useNavigationPrevention = ({ when, pendingAction, setPendingAction }: UseNavigationPreventionOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | number | null>(null);
  const navigationRef = useRef<{ path: string; action: string } | null>(null);
  const isConfirmingRef = useRef(false);
  const previousLocationRef = useRef(location.pathname);
  const { t } = useTranslation();

  // Track when navigation is actually blocked
  useEffect(() => {
    if (!when) {
      setNavigationWasBlocked(false);
    }
  }, [when]);

  // Setup global navigation prevention
  useEffect(() => {
    setupGlobalNavigationPrevention(when, (to, action) => {
      if (when && !isConfirmingRef.current) {
        setPendingNavigation(to);
        setPendingAction(action);
        navigationRef.current = { path: to, action };
        setShowModal(true);
        setNavigationWasBlocked(true);

        window.dispatchEvent(new CustomEvent('navigationBlocked'));
        return true; // Navigation was prevented
      }
      return false; // Navigation was allowed
    });
  }, [when, setPendingAction]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (when) {
        event.preventDefault();
        event.returnValue = t('enrollments.navigationPreventionModalMessage');
        return t('enrollments.navigationPreventionModalMessage');
      }
    };

    if (when) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [when, t]);

  // Override history methods to intercept navigation
  useEffect(() => {
    if (!when) return;

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      if (when && !isConfirmingRef.current) {
        const newPath = args[2] || args[0]?.pathname;
        if (newPath && newPath !== previousLocationRef.current) {
          if (globalNavigationCallback) {
            const wasPrevented = globalNavigationCallback(newPath, 'PUSH');
            if (wasPrevented) {
              return; // Prevent the navigation
            }
          }
        }
      }
      return originalPushState.apply(this, args);
    };

    window.history.replaceState = function (...args) {
      if (when && !isConfirmingRef.current) {
        const newPath = args[2] || args[0]?.pathname;
        if (newPath && newPath !== previousLocationRef.current) {
          if (globalNavigationCallback) {
            const wasPrevented = globalNavigationCallback(newPath, 'REPLACE');
            if (wasPrevented) {
              return; // Prevent the navigation
            }
          }
        }
      }
      return originalReplaceState.apply(this, args);
    };

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [when]);

  // Monitor location changes as a fallback
  useEffect(() => {
    if (!when || isConfirmingRef.current) {
      previousLocationRef.current = location.pathname;
      return;
    }

    // If location changed unexpectedly, it means navigation was attempted
    if (location.pathname !== previousLocationRef.current) {
      // Revert the navigation immediately
      window.history.replaceState(null, '', previousLocationRef.current);

      // Trigger the prevention callback
      if (globalNavigationCallback) {
        globalNavigationCallback(location.pathname, 'PUSH');
      }
    }
  }, [location.pathname, when]);

  // Intercept browser back/forward buttons
  useEffect(() => {
    if (!when) return;

    const handlePopState = (event: PopStateEvent) => {
      if (when && !isConfirmingRef.current) {
        // Prevent the navigation by pushing the current state back
        window.history.pushState(null, '', previousLocationRef.current);

        if (globalNavigationCallback) {
          globalNavigationCallback(window.location.pathname, 'POP');
        }
      }
    };

    // Add an extra state to enable popstate detection
    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [when, location.pathname]);

  const handleConfirmNavigation = useCallback(() => {
    const navigationData = navigationRef.current || { path: pendingNavigation, action: pendingAction };

    if (navigationData.path && typeof navigationData.path === 'string') {
      isConfirmingRef.current = true;

      setShowModal(false);
      setNavigationWasBlocked(false);

      setPendingNavigation(null);
      setPendingAction(null);
      navigationRef.current = null;

      const path = navigationData.path as string;
      if (navigationData.action === 'PUSH') {
        navigate(path);
      } else if (navigationData.action === 'REPLACE') {
        navigate(path, { replace: true });
      } else {
        navigate(path);
      }

      setTimeout(() => {
        isConfirmingRef.current = false;
      }, 100);
    } else {
      setShowModal(false);
    }
  }, [pendingNavigation, setPendingAction, pendingAction, navigate]);

  const handleCancelNavigation = useCallback(() => {
    setShowModal(false);
    setNavigationWasBlocked(false);
    setPendingNavigation(null);
    setPendingAction(null);
    navigationRef.current = null;
  }, [setPendingAction]);

  const navigateWithPrevention = useCallback(
    (to: string | number) => {
      if (when) {
        setPendingNavigation(to);
        setShowModal(true);
        return false;
      }

      if (typeof to === 'string') {
        navigate(to);
      } else {
        // For number navigation (go back/forward), we need to use window.history
        window.history.go(to);
      }
      return true;
    },
    [when, navigate]
  );

  return {
    showModal,
    handleConfirmNavigation,
    handleCancelNavigation,
    navigate: navigateWithPrevention,
    modalProps: {
      isOpen: showModal,
      onClose: handleCancelNavigation,
      onConfirm: handleConfirmNavigation,
    },
  };
};
